/**
 * 07 消息中心 `/inbox`（底部导航第二项）+ 10 空态
 * -----------------------------------------------------------------------------
 * 版面按设计稿「学员端 · 消息中心」：筛选胶囊 → 消息卡列表（未读红点 + 标题 +
 * 时间 + 正文 + 送达级别 + 动作按钮）。空态用设计稿的「铃铛圆 + 去看看我的报名」。
 *
 * 接真实端点（`GET /api/h5/inbox` + `POST /{id}/read`，服务端已实现）。
 * 附件4 §04 的四件事：
 *   - 送达级别在列表上直接可见（黄 / 绿 / 灰点，见 `inbox-level.ts`）
 *   - 未读条目高亮，点击即标记已读（标记后列表与角标一起刷新）
 *   - **每条消息带动作按钮直接跳办**（跳转到该消息指向的那一单，路由见 `inbox-action.ts`）
 *   - 无记录时展示空态页
 *
 * ⚠️ 动作按钮与卡片主体是**兄弟节点**而不是嵌套：`<button>` 里套 `<button>`
 * 是非法 HTML，浏览器会自作主张地拆开 DOM，点击命中区随之错乱。
 * 设计稿右上角的「全部已读」一期没有批量端点，因此不落按钮（不扩范围）。
 */
import { Toast } from 'antd-mobile';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { describeApiError } from '../api/errors';
import { useInbox, useMarkInboxRead } from '../api/hooks';
import { Button, EmptyHint, ErrorBlock, LoadingBlock, PageShell } from '../components/layout';
import { inboxActionOf } from './inbox-action';
import { inboxLevelOf, LEVEL_DOT_CLASS } from './inbox-level';

type Filter = 'all' | 'unread' | 'must';

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay
    ? `今天 ${pad(date.getHours())}:${pad(date.getMinutes())}`
    : `${date.getMonth() + 1}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function Inbox() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [markingAll, setMarkingAll] = useState(false);
  const inbox = useInbox();
  const markRead = useMarkInboxRead();

  const items = inbox.data?.items ?? [];

  const counts = useMemo(() => {
    const unreadCount = items.filter((item) => item.readAt === null).length;
    const mustCount = items.filter((item) => inboxLevelOf(item.eventCode) === 'L1').length;
    return { all: items.length, unread: unreadCount, must: mustCount };
  }, [items]);

  const unreadIds = useMemo(
    () => items.filter((item) => item.readAt === null).map((item) => item.id),
    [items],
  );

  /**
   * 全部已读：服务端没有批量端点，这里对未读逐条调用既有的 `POST /{id}/read`。
   * 不新增接口、也不在前端伪造「已读」状态（失败要如实提示）。
   */
  const markAll = async () => {
    if (markingAll || unreadIds.length === 0) return;
    setMarkingAll(true);
    try {
      await Promise.all(unreadIds.map((id) => markRead(id)));
      Toast.show({ icon: 'success', content: '已全部标记为已读' });
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setMarkingAll(false);
    }
  };

  const visible = useMemo(() => {
    if (filter === 'unread') return items.filter((item) => item.readAt === null);
    if (filter === 'must') return items.filter((item) => inboxLevelOf(item.eventCode) === 'L1');
    return items;
  }, [items, filter]);

  return (
    <PageShell
      title="消息中心"
      back
      tabbar
      layout="tab"
      right={
        unreadIds.length > 0 ? (
          <button type="button" className="navbar__action" onClick={() => void markAll()}>
            {markingAll ? '处理中…' : '全部已读'}
          </button>
        ) : undefined
      }
    >
      <div className="chip-row" role="tablist" aria-label="消息筛选">
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'all'}
          className={`chip${filter === 'all' ? ' chip--active' : ''}`}
          onClick={() => setFilter('all')}
        >
          全部 <span className="mono-num">{counts.all}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'unread'}
          className={`chip${filter === 'unread' ? ' chip--active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          未读 <span className="mono-num">{counts.unread}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'must'}
          className={`chip${filter === 'must' ? ' chip--active' : ''}`}
          onClick={() => setFilter('must')}
        >
          必达 <span className="mono-num">{counts.must}</span>
        </button>
      </div>

      {inbox.isPending ? <LoadingBlock text="正在加载消息" /> : null}

      {inbox.isError ? (
        <ErrorBlock
          message={`消息加载失败：${describeApiError(inbox.error)}`}
          onRetry={() => inbox.refetch()}
        />
      ) : null}

      {!inbox.isPending && !inbox.isError && items.length === 0 ? (
        <EmptyHint
          title="暂无消息通知"
          description="报名提交后，进度、缴费提醒与发放通知都会在这里留档"
          actionText="去看看我的报名"
          onAction={() => navigate('/registration/list')}
          fill
        />
      ) : null}

      {!inbox.isPending && !inbox.isError && items.length > 0 && visible.length === 0 ? (
        <EmptyHint
          title={filter === 'unread' ? '没有未读消息' : '没有必达消息'}
          description="切换上方筛选可以查看全部留档记录"
        />
      ) : null}

      <ul className="inbox-list">
        {visible.map((message) => {
          const unreadItem = message.readAt === null;
          const action = inboxActionOf(message.eventCode, message.registrationId);
          const level = inboxLevelOf(message.eventCode);
          return (
            <li key={message.id}>
              <div className={`inbox-item${unreadItem ? ' inbox-item--unread' : ''}`}>
                <button
                  type="button"
                  className="inbox-item__main"
                  onClick={() => {
                    if (unreadItem) void markRead(message.id);
                  }}
                >
                  <span className="inbox-item__head">
                    {unreadItem ? <span className="inbox-item__dot" /> : null}
                    <span className="inbox-item__title">{message.title}</span>
                    <span className="inbox-item__time mono-num">
                      {formatTime(message.createdAt)}
                    </span>
                  </span>
                  <span className="inbox-item__body">{message.content}</span>
                </button>
                <div className="inbox-item__foot">
                  {/* 级别圆点黄/绿/灰三色常显（附件4 §04）；「必达」胶囊只挂 L1，避免关键通知被淹没 */}
                  <span className="inbox-item__foot-left">
                    <span className={LEVEL_DOT_CLASS[level]} aria-hidden="true" />
                    {level === 'L1' ? <span className="inbox-tag">必达</span> : null}
                  </span>
                  {action ? (
                    <Button variant="soft" size="mini" onClick={() => navigate(action.route)}>
                      {action.label}
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </PageShell>
  );
}
